import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    X,
    Loader2,
    AlertTriangle,
    RefreshCw,
    ChevronDown,
    Phone,
    Brain,
    Mic2,
    Settings2,
    CheckCircle2,
} from 'lucide-react';
import patientCallingService from '../../../services/patientCalling.service';
import Toast from '../../Common/Toast';
import styles from './CreateConfigModal.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────

interface DropdownOption {
    id: string | number;
    name: string;
    slug?: string;
    phone_number?: string;
}

interface MasterData {
    callTypes: DropdownOption[];
    entityTypes: DropdownOption[];
    llmProviders: DropdownOption[];
    ttsProviders: DropdownOption[];
    ivrMenus: DropdownOption[];
}

interface FormState {
    calling_system_call_type: string;
    calling_system_entity_type: string;
    llm_provider: string;
    tts_provider: string;
    extra_parameters: string; // raw JSON string for editing
}

interface FormErrors {
    calling_system_call_type?: string;
    calling_system_entity_type?: string;
    extra_parameters?: string;
}

interface ToastState {
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

interface Props {
    onClose: () => void;
    onSaved?: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function normalizeOptions(raw: unknown[]): DropdownOption[] {
    if (!Array.isArray(raw)) return [];
    const result: DropdownOption[] = [];
    for (const item of raw) {
        if (typeof item !== 'object' || item === null) continue;
        const obj = item as Record<string, unknown>;
        const id = obj.id ?? obj.slug ?? obj.name;
        const name = obj.name ?? obj.slug ?? obj.id;
        if (!id || !name) continue;
        result.push({
            id: id as string | number,
            name: String(name),
            slug: obj.slug as string | undefined,
            phone_number: obj.phone_number as string | undefined,
        });
    }
    return result;
}

function normalizeLLMProviders(raw: unknown[]): DropdownOption[] {
    if (!Array.isArray(raw)) return [];
    const result: DropdownOption[] = [];
    for (const item of raw) {
        if (typeof item !== 'object' || item === null) continue;
        const obj = item as Record<string, unknown>;
        const provider = obj.provider as string | undefined;
        const model = obj.model_name as string | undefined;
        if (!provider || !model) continue;
        result.push({
            id: `${provider}_${model}`,
            name: `${provider} (${model})`,
            slug: model,
        });
    }
    return result;
}

function normalizeTTSProviders(raw: unknown[]): DropdownOption[] {
    if (!Array.isArray(raw)) return [];
    const result: DropdownOption[] = [];
    for (const item of raw) {
        if (typeof item !== 'object' || item === null) continue;
        const obj = item as Record<string, unknown>;
        const provider = obj.provider as string | undefined;
        const voice = obj.voice_name as string | undefined;
        if (!provider || !voice) continue;
        result.push({
            id: `${provider}_${voice}`,
            name: `${provider} (${voice})`,
            slug: voice,
        });
    }
    return result;
}

function getOptionValue(option: DropdownOption): string {
    return (option.slug ?? option.phone_number ?? String(option.name)) as string;
}

function isJsonValid(str: string): boolean {
    if (!str.trim()) return true;
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
}

// ─── Sub-components ─────────────────────────────────────────────────────────

interface SelectFieldProps {
    label: string;
    icon: React.ReactNode;
    value: string;
    options: DropdownOption[];
    placeholder: string;
    required?: boolean;
    error?: string;
    onChange: (val: string) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({
    label, icon, value, options, placeholder, required, error, onChange,
}) => {
    const selectClass = [
        styles.select,
        value ? styles.selectFilled : styles.selectPlaceholder,
        error ? styles.selectError : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={styles.field}>
            <label className={styles.label}>
                <span className={styles.labelIcon}>{icon}</span>
                {label}
                {required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.selectWrap}>
                <select
                    className={selectClass}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="" disabled>{placeholder}</option>
                    {options.map((opt) => (
                        <option key={opt.id} value={getOptionValue(opt)}>
                            {opt.phone_number ?? opt.name}
                        </option>
                    ))}
                </select>
                <ChevronDown size={15} className={styles.selectChevron} />
            </div>
            {error && (
                <span className={styles.fieldError}>
                    <AlertTriangle size={11} /> {error}
                </span>
            )}
        </div>
    );
};

interface SectionHeaderProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, subtitle }) => (
    <div className={styles.sectionHeader}>
        <div className={styles.sectionIcon}>{icon}</div>
        <div>
            <div className={styles.sectionTitle}>{title}</div>
            <div className={styles.sectionSubtitle}>{subtitle}</div>
        </div>
    </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const CreateConfigModal: React.FC<Props> = ({ onClose, onSaved }) => {
    const [loadState, setLoadState] = useState<LoadState>('loading');
    const [masterData, setMasterData] = useState<MasterData>({
        callTypes: [], entityTypes: [], llmProviders: [], ttsProviders: [], ivrMenus: [],
    });
    const [loadError, setLoadError] = useState<string | null>(null);

    const [form, setForm] = useState<FormState>({
        calling_system_call_type: '',
        calling_system_entity_type: '',
        llm_provider: '',
        tts_provider: '',
        extra_parameters: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastState | null>(null);

    const abortRef = useRef(false);

    const loadMasterData = useCallback(async () => {
        setLoadState('loading');
        setLoadError(null);
        abortRef.current = false;

        try {
            const [callTypes, entityTypes, llm, tts, ivr] = await Promise.all([
                patientCallingService.getCallTypes(),
                patientCallingService.getEntityTypes(),
                patientCallingService.getLLMProviders(),
                patientCallingService.getTTSProviders(),
                patientCallingService.getIVRMenus(),
            ]);

            if (abortRef.current) return;

            setMasterData({
                callTypes:    normalizeOptions(callTypes   as unknown[]),
                entityTypes:  normalizeOptions(entityTypes as unknown[]),
                llmProviders: normalizeLLMProviders(llm    as unknown[]),
                ttsProviders: normalizeTTSProviders(tts    as unknown[]),
                ivrMenus:     normalizeOptions(ivr         as unknown[]),
            });
            setLoadState('ready');
        } catch (e: unknown) {
            if (abortRef.current) return;
            const msg = e instanceof Error ? e.message : 'Failed to load configuration data';
            setLoadError(msg);
            setLoadState('error');
        }
    }, []);

    useEffect(() => {
        loadMasterData();
        return () => { abortRef.current = true; };
    }, [loadMasterData]);

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        }
    };

    const validate = (): boolean => {
        const next: FormErrors = {};
        if (!form.calling_system_call_type)
            next.calling_system_call_type = 'Call type is required';
        if (!form.calling_system_entity_type)
            next.calling_system_entity_type = 'Entity type is required';
        if (form.extra_parameters && !isJsonValid(form.extra_parameters))
            next.extra_parameters = 'Must be valid JSON';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const isFormComplete =
        !!form.calling_system_call_type && !!form.calling_system_entity_type;

    const handleSubmit = async () => {
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload: Record<string, unknown> = {
                calling_system_call_type:   form.calling_system_call_type,
                calling_system_entity_type: form.calling_system_entity_type,
            };
            if (form.llm_provider) payload.llm_provider = form.llm_provider;
            if (form.tts_provider) payload.tts_provider = form.tts_provider;
            if (form.extra_parameters.trim()) {
                payload.extra_parameters = JSON.parse(form.extra_parameters);
            }

            await patientCallingService.createConfig(payload);

            setToast({ type: 'success', message: 'Configuration saved successfully' });
            setTimeout(() => {
                onSaved?.();
                onClose();
            }, 900);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save configuration';
            setToast({ type: 'error', message: msg });
        } finally {
            setSubmitting(false);
        }
    };

    const handleOverlayClick = () => {
        if (!submitting) onClose();
    };

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* ── Header ── */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.headerIcon}>
                            <Settings2 size={18} />
                        </div>
                        <div>
                            <h2 className={styles.title}>Calling Configuration</h2>
                            <p className={styles.subtitle}>
                                Set up your AI-powered calling system
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeBtn}
                        onClick={onClose}
                        disabled={submitting}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className={styles.body}>
                    {loadState === 'loading' && (
                        <div className={styles.stateWrap}>
                            <div
                                className={`${styles.stateIcon} ${styles.stateIconLoading}`}
                            >
                                <Loader2 size={22} className={styles.spinning} />
                            </div>
                            <div>
                                <p className={styles.stateTitle}>
                                    Loading configuration options
                                </p>
                                <p className={styles.stateMessage}>
                                    Fetching call types, providers, and IVR menus…
                                </p>
                            </div>
                        </div>
                    )}

                    {loadState === 'error' && (
                        <div className={styles.stateWrap}>
                            <div
                                className={`${styles.stateIcon} ${styles.stateIconError}`}
                            >
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <p className={styles.stateTitle}>Failed to load options</p>
                                <p className={styles.stateMessage}>{loadError}</p>
                            </div>
                            <button className={styles.retryBtn} onClick={loadMasterData}>
                                <RefreshCw size={14} /> Retry
                            </button>
                        </div>
                    )}

                    {loadState === 'ready' && (
                        <>
                            {/* ── Section: Calling System ── */}
                            <div className={styles.section}>
                                <SectionHeader
                                    icon={<Phone size={14} />}
                                    title="Calling System"
                                    subtitle="Configure how calls are classified and routed"
                                />
                                <SelectField
                                    label="Call Type"
                                    icon={<Phone size={12} />}
                                    value={form.calling_system_call_type}
                                    options={masterData.callTypes}
                                    placeholder="Select a call type..."
                                    required
                                    error={errors.calling_system_call_type}
                                    onChange={(v) => setField('calling_system_call_type', v)}
                                />
                                <SelectField
                                    label="Entity Type"
                                    icon={<Settings2 size={12} />}
                                    value={form.calling_system_entity_type}
                                    options={masterData.entityTypes}
                                    placeholder="Select an entity type..."
                                    required
                                    error={errors.calling_system_entity_type}
                                    onChange={(v) => setField('calling_system_entity_type', v)}
                                />
                            </div>

                            {/* ── Section: AI Configuration ── */}
                            <div className={styles.section}>
                                <SectionHeader
                                    icon={<Brain size={14} />}
                                    title="AI Configuration"
                                    subtitle="Choose language model and text-to-speech engine"
                                />
                                <SelectField
                                    label="LLM Provider"
                                    icon={<Brain size={12} />}
                                    value={form.llm_provider}
                                    options={masterData.llmProviders}
                                    placeholder="Select LLM provider..."
                                    onChange={(v) => setField('llm_provider', v)}
                                />
                                <SelectField
                                    label="TTS Provider"
                                    icon={<Mic2 size={12} />}
                                    value={form.tts_provider}
                                    options={masterData.ttsProviders}
                                    placeholder="Select TTS provider..."
                                    onChange={(v) => setField('tts_provider', v)}
                                />
                            </div>

                            {/* ── Section: General ── */}
                            <div className={styles.section}>
                                <SectionHeader
                                    icon={<Settings2 size={14} />}
                                    title="General Settings"
                                    subtitle="Phone formatting and advanced parameters"
                                />
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        <span className={styles.labelIcon}>
                                            <Settings2 size={12} />
                                        </span>
                                        Extra Parameters
                                        <span className={styles.optional}>— optional</span>
                                    </label>
                                    <textarea
                                        className={`${styles.textarea} ${
                                            errors.extra_parameters ? styles.textareaError : ''
                                        }`}
                                        value={form.extra_parameters}
                                        onChange={(e) => {
                                            setField('extra_parameters', e.target.value);
                                            if (errors.extra_parameters) {
                                                setErrors((prev) => ({ ...prev, extra_parameters: undefined }));
                                            }
                                        }}
                                        onBlur={() => {
                                            if (form.extra_parameters && !isJsonValid(form.extra_parameters)) {
                                                setErrors((prev) => ({ ...prev, extra_parameters: 'Must be valid JSON' }));
                                            }
                                        }}
                                        placeholder={'{\n  "key": "value"\n}'}
                                        rows={4}
                                    />
                                    {errors.extra_parameters ? (
                                        <span className={styles.fieldError}>
                                            <AlertTriangle size={11} /> {errors.extra_parameters}
                                        </span>
                                    ) : (
                                        <span className={styles.fieldHint}>
                                            Must be valid JSON. Merged into every call payload.
                                        </span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                {loadState === 'ready' && (
                    <div className={styles.footer}>
                        <span className={styles.footerHint}>
                            <span className={styles.required}>*</span> Required fields
                        </span>
                        <div className={styles.footerActions}>
                            <button
                                className={styles.outlineBtn}
                                onClick={onClose}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.primaryBtn}
                                onClick={handleSubmit}
                                disabled={submitting || !isFormComplete}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={14} className={styles.spinning} />
                                        Saving…
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 size={14} />
                                        Save Configuration
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Toast ── */}
                {toast && (
                    <div className={styles.toastWrap}>
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast(null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateConfigModal;
